import { useEffect, useRef, useState } from "react";

/**
 * HoverCyclePhoto — auto-cycles through a listing's photos while the mouse
 * is over the card. On mouse-leave it resets to the first photo. Falls back
 * gracefully when only 0 or 1 photos exist.
 *
 * Props:
 *   photos   — string[] — array of photo URLs (first is the default)
 *   alt      — string   — alt text
 *   interval — number   — ms between transitions (default 900)
 *   className — string  — passed through to <img>
 */
export default function HoverCyclePhoto({
  photos = [],
  alt = "",
  interval = 900,
  className = "",
}) {
  const [idx, setIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!hovering || photos.length < 2) {
      clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % photos.length);
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [hovering, photos.length, interval]);

  if (!photos.length) {
    return (
      <div className={`bg-stone-100 flex items-center justify-center text-gray-300 text-sm ${className}`}>
        No photo
      </div>
    );
  }

  return (
    <div
      className="w-full h-full relative"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setIdx(0); }}
    >
      <img
        src={photos[idx]}
        alt={alt}
        loading="lazy"
        className={`${className} transition-opacity duration-300`}
      />
      {photos.length > 1 && hovering && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none">
          {photos.slice(0, Math.min(photos.length, 8)).map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === idx % Math.min(photos.length, 8) ? "w-4 bg-white" : "w-1.5 bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
